import { useMemo, useCallback, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import NsFormGroup from '@britishgasenergy/react-nucleus/dist/nuc-components/NsFormGroup';

import { Error } from 'components/common/error/Error';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { NsCard } from 'components/common/ns-card/NsCard';
import { NsForm } from 'components/common/ns-form/NsForm';
import {
  selectDetails,
  selectDirectDebitErrors,
  selectIsLoadingDirectDebit,
  selectHasMaxAttemptsError,
  setDirectDebitField,
  resetDetails as resetDirectDebitDetails
} from 'state/direct-debit/direct-debit-slice';
import { getDirectDebitPaymentTokenThunk } from 'state/direct-debit/direct-debit-thunks';
import { DirectDebitForm } from 'components/direct-sales/direct-debit-revamp/components/DirectDebitForm';
import { DirectDebitGuarantee } from 'components/direct-sales/direct-debit-revamp/components/DirectDebitGuarantee';
import { DirectDebitCompanyForm } from 'components/direct-sales/direct-debit-revamp/components/DirectDebitCompanyForm';
import { NsStopgap } from 'components/common/ns-stopgap/NsStopgap';
import { useValidateBankAccount } from 'components/direct-sales/direct-debit-revamp/hooks/useValidateBankAccount';
import { useDirectDebitVerificationError } from 'components/direct-sales/direct-debit-revamp/hooks/useDirectDebitVerificationError';
import { useNavigation } from 'hooks/useNavigation';
import { usePartner } from 'components/app/routes/usePartner';
import { DirectDebitPromotionExpander } from './DirectDebitPromotionExpander';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useStateMachineContext } from 'machines/hooks/useStateMachineContext';

export const directDebitContentTestId = 'directDebitContentTestId';

export const DirectDebitContent = () => {
  const { actor } = useStateMachineContext();
  const dispatch = useAppDispatch();
  const { isDSJourney } = usePartner();
  const errors = useSelector(selectDirectDebitErrors, shallowEqual);
  const isLoading = useSelector(selectIsLoadingDirectDebit, shallowEqual);
  const details = useSelector(selectDetails, shallowEqual);
  const hasMaxAttemptsError = useSelector(
    selectHasMaxAttemptsError,
    shallowEqual
  );

  const { isCommercial, accountHolderName, sortCode, accountNumber } = details;
  const validateBankAccount = useValidateBankAccount();
  const handleDirectDebitVerificationError = useDirectDebitVerificationError();
  const { eventTypes, next } = useNavigation();

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (hasMaxAttemptsError) {
        return;
      }

      try {
        await validateBankAccount();

        const bankAccountData = {
          sortCode: sortCode,
          accountNumber: accountNumber,
          accountHolder: accountHolderName
        };
        await dispatch(getDirectDebitPaymentTokenThunk(bankAccountData));
        next({ eventType: eventTypes.NEXT });
      } catch (error) {
        const e = handleDirectDebitVerificationError(error);
        e && console.error(e);
      }
    },
    [
      dispatch,
      validateBankAccount,
      handleDirectDebitVerificationError,
      hasMaxAttemptsError,
      sortCode,
      accountNumber,
      accountHolderName,
      next,
      eventTypes.NEXT
    ]
  );

  const handleChange = useCallback(
    (event) => {
      const {
        target: { name, processedValue: value }
      } = event;

      dispatch(setDirectDebitField({ name, value }));
    },
    [dispatch]
  );

  // BUG 153994: reset direct debit details on back navigation.
  // `actor` will be recreated on back navigation
  useEffect(() => {
    dispatch(resetDirectDebitDetails());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor]);

  return useMemo(() => {
    if (isLoading) {
      return <NsStopgap />;
    }

    return (
      <NsCard testId={directDebitContentTestId}>
        <NsForm onSubmit={handleSubmit}>
          <NsFormGroup legend="Account holder details">
            {!isCommercial ? (
              <DirectDebitForm handleChange={handleChange} details={details} />
            ) : (
              <DirectDebitCompanyForm
                handleChange={handleChange}
                details={details}
              />
            )}
          </NsFormGroup>
          <DirectDebitGuarantee />
          <Error errors={errors} />
          <NsPrimaryCta isLoading={isLoading}>Check your order</NsPrimaryCta>
          <br />
          {isDSJourney && <DirectDebitPromotionExpander />}
        </NsForm>
      </NsCard>
    );
  }, [
    handleSubmit,
    handleChange,
    errors,
    isLoading,
    isCommercial,
    details,
    isDSJourney
  ]);
};

export default DirectDebitContent;
